import { fontFamily, mediaQueriesBP } from "@hardware/Utilities/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import parse from "html-react-parser";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    alignment = "alignment",
    title = "title",
    description = "description",
    placeholder = "placeholder",
    buttonLabel = "buttonLabel",
    buttonNext = "buttonNext",

    displayAlignment = "Layout",
    displayTitle = "Title",
    displayDescription = "Description",
    displayPlaceholder = "Placeholder Text",
    displayButtonLabel = "Button Label",
    displayButtonNext = "Button is next to input?",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.placeholder]: string;
        [fields.alignment]: string;
        [fields.buttonLabel]: string;
        [fields.buttonNext]: string;
    };
}

export const mapStateToProps = (_: ApplicationState, __: OwnProps) => {
    return {};
};

const mapDispatchToProps = {
    subscribe,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SubscribeToBlogWidgetStyles {
    mainGridContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
}

export const subscribeToBlogWidgetStyles: SubscribeToBlogWidgetStyles = {
    mainGridContainer: {
        gap: 10,
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    titleGridItem: {
        width: 12,
    },
    titleLabel: {
        variant: "p",
        forwardAs: "h4",
        fontFamily,
        size: "18px",
        lineHeight: "24px",
        weight: 500,
        css: css`
            margin-bottom: 0;
        `,
    },
    descriptionGridItem: {
        width: 12,
    },
    descriptionText: {
        variant: "p",
        size: "16px",
        lineHeight: "24px",
        css: css`
            margin-bottom: 5px;
        `,
    },
    emailGridItem: {
        width: 12,
    },
    emailTextField: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    emailButton: {
        mergeCss: true,
        variant: "primary",
        css: css`
            width: 100%;
            padding: 0 10px;
        `,
    },
};

const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");

export const SubscribeToBlogWidget = ({ fields, subscribe, id }: Props) => {
    const emailRequiredFieldMessage = siteMessage("CustomBlog_SubscribeToBlog_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("CustomBlog_SubscribeToBlog_EmailIsInvalidErrorMessage");

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const styles = useMergeStyles("subscribe", subscribeToBlogWidgetStyles);

    const onSubscribeClick = () => {
        setIsSubmitted(true);
        if (!validateEmail(email)) {
            return;
        }

        subscribe({
            email,
            onSuccess: () => {
                toasterContext.addToast({
                    body: siteMessage("CustomBlog_SubscribeToBlog_Success"),
                    messageType: "success",
                });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                     
                    this.onSuccess?.();
                }
            },
        });
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const gridItemCss = (gridItemProps?: GridItemProps) => {
        return css`
            justify-content: ${fields.alignment ? fields.alignment : "center"};
            ${gridItemProps?.css}
        `;
    };

    const inputWrapper = css`
        display: flex;
        flex-wrap: wrap;
        width: 100%;
        ${fields.buttonNext ? "" : "flex-direction: column;"}
        ${mediaQueriesBP.maxSM} {
            ${fields.buttonNext ? "min-width: 220px;" : ""}
        }
        ${mediaQueriesBP.minSM} {
            ${fields.buttonNext ? "min-width: 380px;" : ""}
        }
    `;

    const inputFieldCss = css`
        width: 100%;
        ${mediaQueriesBP.maxSM} {
            ${fields.buttonNext ? "max-width: 220px;" : ""}
        }
        ${mediaQueriesBP.minSM} {
            ${fields.buttonNext ? "max-width: 210px;" : ""}
        }
    `;

    const buttonCss = css`
        width: 100%;
        ${mediaQueriesBP.maxSM} {
            margin-top: 10px;
            ${fields.buttonNext ? "max-width: 220px;" : ""}
        }
        ${mediaQueriesBP.minSM} {
            ${fields.buttonNext ? "margin-left: 10px;" : "margin-top: 10px;"}
            ${fields.buttonNext ? "max-width: 160px;" : ""}
        }
    `;

    return (
        <GridContainer {...styles.mainGridContainer}>
            {fields.title && fields.title.length > 0 && (
                <GridItem {...styles.titleGridItem} css={gridItemCss(styles.titleGridItem)}>
                    <Typography {...styles.titleLabel}>{fields.title}</Typography>
                </GridItem>
            )}
            {fields.description && fields.description.trim().length > 0 && (
                <GridItem {...styles.descriptionGridItem} css={gridItemCss(styles.descriptionGridItem)}>
                    <Typography {...styles.descriptionText}>{parse(fields.description, parserOptions)}</Typography>
                </GridItem>
            )}
            <GridItem {...styles.emailGridItem} css={gridItemCss(styles.emailGridItem)}>
                <StyledWrapper css={inputWrapper}>
                    <StyledWrapper css={inputFieldCss}>
                        <TextField
                            {...styles.emailTextField}
                            id={id}
                            placeholder={fields.placeholder}
                            value={email}
                            onChange={e => emailChangeHandler(e.currentTarget.value)}
                            error={isSubmitted && emailError}
                        />
                    </StyledWrapper>
                    <StyledWrapper css={buttonCss}>
                        <Button
                            {...styles.emailButton}
                            onClick={onSubscribeClick}
                            disabled={isSubmitted && !!emailError}
                        >
                            {fields.buttonLabel}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(SubscribeToBlogWidget) as any,
    definition: {
        displayName: "Subscribe to Blog",
        group: "Verndale Box - Blog" as any,
        icon: "bell",
        allowedContexts: ["BlogLandingPage", "BlogCategoryPage", "BlogDetailPage"] as any,
        fieldDefinitions: [
            {
                name: fields.alignment,
                displayName: fields.displayAlignment,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "flex-start",
                fieldType: "Translatable",
            },
            {
                name: fields.title,
                displayName: fields.displayTitle,
                editorTemplate: "TextField",
                defaultValue: "Subscribe Now",
                fieldType: "Translatable",
            },
            {
                name: fields.description,
                displayName: fields.displayDescription,
                editorTemplate: "MultilineTextField",
                defaultValue: "Subscribe and be the first to know when new articles are published.",
                fieldType: "Translatable",
            },
            {
                name: fields.placeholder,
                displayName: fields.displayPlaceholder,
                editorTemplate: "TextField",
                defaultValue: "Email Address",
                fieldType: "Translatable",
            },
            {
                name: fields.buttonLabel,
                displayName: fields.displayButtonLabel,
                editorTemplate: "TextField",
                defaultValue: "Subscribe to Blog",
                fieldType: "Translatable",
            },
            {
                name: fields.buttonNext,
                displayName: fields.displayButtonNext,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
